import {Pipe, PipeTransform} from '@angular/core';
import {Store} from '@ngrx/store';
import {map, Observable} from 'rxjs';
import {DateUtils} from '../../../../shared-utilities/utils-old/shared-utils-old/date-utils';
import {selectApplicationPreferenceSettings} from '../../../../features/settings-new/store/setting.selectors';
import {LocaleDateFormatEnum, TLocaleDateFormat} from '../../../../features/settings-new/models';

/**
 * A pipe that formats dates according to the user's date format preference stored in the application settings.
 * Supports Date objects, strings, timestamps, and Firebase Timestamp objects.
 *
 * @example
 *  {{ myDate | gallixDate }}
 *  // Output: Formatted according to user's preference
 *
 * @example
 *  {{ myDate | gallixDate:'DATETIME_MEDIUM' }}
 *  // Output: Formatted using the specified format (overrides user preference)
 */
@Pipe({
  name: 'gallixDate',
  standalone: false,
})
export class GallixDatePipe implements PipeTransform {
  private dateFormat$: Observable<TLocaleDateFormat>;

  constructor(private readonly store: Store) {
    this.dateFormat$ = this.store.select(selectApplicationPreferenceSettings).pipe(
      map(preferences => preferences?.dateFormat ?? LocaleDateFormatEnum.DEFAULT),
    );
  }

  /**
   * Transforms a date value into a formatted string according to user preferences or specified format.
   *
   * @param value The date value to format (Date, string, timestamp, or Firebase Timestamp)
   * @param format Optional format override. If not provided, uses user's preference from settings.
   * @returns Observable<string | null> An observable that emits the formatted date string
   */
  transform(
    value: Date | string | number | {seconds: number; nanoseconds: number} | null | undefined,
    format?: TLocaleDateFormat,
  ): Observable<string | null> {
    if (!value) {
      return new Observable(observer => {
        observer.next('No Date Found');
        observer.complete();
      });
    }

    // If format is provided, use it directly without observing store
    if (format) {
      return new Observable(observer => {
        observer.next(this.formatDate(value, format));
        observer.complete();
      });
    }

    // Otherwise, observe the user's date format preference
    return this.dateFormat$.pipe(
      map(dateFormat => this.formatDate(value, dateFormat)),
    );
  }

  /**
   * Formats the date value using DateUtils.
   *
   * @param value The date value to format
   * @param format The date format to use
   * @returns The formatted date string or null if invalid
   */
  private formatDate(
    value: Date | string | number | {seconds: number; nanoseconds: number},
    format: TLocaleDateFormat,
  ): string | null {
    // Handle Firebase Timestamp format
    if (value && typeof value === 'object' && 'seconds' in value) {
      const ms = value.seconds * 1000 + (value.nanoseconds ?? 0) / 1e6;
      return DateUtils.dateToString(new Date(ms), format);
    }

    // Handle Date, string, or number
    if (value instanceof Date) {
      return DateUtils.dateToString(value, format);
    }

    // At this point, value is string or number
    return DateUtils.dateToString(new Date(value as string | number), format);
  }
}
